import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { Search, Plus, Filter, Users, ChevronRight, WifiOff } from "lucide-react";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { cacheData, getCachedData } from "@/utils/offlineQueue";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RISK_LABELS = { critico: "Crítico", alto: "Alto", medio: "Médio", baixo: "Baixo" };
const CACHE_KEY = "patients_list";

export default function PatientsPage() {
  const navigate = useNavigate();
  const { isOnline } = useOnlineStatus();
  const [patients, setPatients] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [fromCache, setFromCache] = useState(false);
  const limit = 20;

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    if (!isOnline) {
      const cached = getCachedData(CACHE_KEY);
      if (cached) {
        setPatients(cached.patients || []);
        setTotal(cached.total || 0);
        setFromCache(true);
      }
      setLoading(false);
      return;
    }
    try {
      const resp = await axios.get(`${API}/patients`, {
        params: { search, skip: page * limit, limit },
        withCredentials: true,
      });
      setPatients(resp.data.patients || []);
      setTotal(resp.data.total || 0);
      setFromCache(false);
      // Cache for offline use (only first page, no search)
      if (!search && page === 0) {
        cacheData(CACHE_KEY, resp.data);
      }
    } catch (e) {
      // Fallback to cache on network error
      const cached = getCachedData(CACHE_KEY);
      if (cached) {
        setPatients(cached.patients || []);
        setTotal(cached.total || 0);
        setFromCache(true);
      }
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search, page, isOnline]);

  useEffect(() => {
    const t = setTimeout(fetchPatients, 300);
    return () => clearTimeout(t);
  }, [fetchPatients]);

  return (
    <Layout>
      <div data-testid="patients-page" className="space-y-5">
        {/* Offline banner */}
        {(!isOnline || fromCache) && (
          <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-xl px-4 py-2.5 text-xs text-orange-700 animate-fade-in" data-testid="offline-patients-banner">
            <WifiOff className="w-3.5 h-3.5 flex-shrink-0" />
            {!isOnline
              ? "Modo offline — exibindo dados em cache. Novos pacientes serão sincronizados ao reconectar."
              : "Dados em cache — reconectando..."}
          </div>
        )}
        {/* Header */}
        <div className="flex items-center justify-between animate-fade-in">
          <div>
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              Pacientes
            </h1>
            <p className="text-sm text-muted-foreground">{total} paciente(s) cadastrado(s)</p>
          </div>
          <button
            onClick={() => navigate("/patients/new")}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-all"
            data-testid="add-patient-button"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Novo Paciente</span>
          </button>
        </div>

        {/* Search */}
        <div className="relative animate-fade-in">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar por nome ou CPF..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="w-full pl-10 pr-4 py-3 bg-white border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            data-testid="patient-search-input"
          />
        </div>

        {/* Patient list */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-20 rounded-xl bg-white border border-border animate-pulse" />
            ))}
          </div>
        ) : patients.length === 0 ? (
          <div className="bg-white rounded-xl border border-border p-12 text-center animate-fade-in">
            <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-foreground mb-1" style={{ fontFamily: 'Manrope' }}>
              {search ? "Nenhum resultado" : "Nenhum paciente cadastrado"}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {search ? `Nenhum paciente encontrado para "${search}"` : "Comece cadastrando o primeiro paciente"}
            </p>
            {!search && (
              <button
                onClick={() => navigate("/patients/new")}
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium"
                data-testid="empty-add-patient-btn"
              >
                <Plus className="w-4 h-4" />
                Novo Paciente
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2 stagger-children">
            {patients.map((p) => (
              <div
                key={p.patient_id}
                className="bg-white rounded-xl border border-border p-4 flex items-center justify-between cursor-pointer hover-lift transition-all animate-fade-in"
                onClick={() => navigate(`/patients/${p.patient_id}`)}
                data-testid={`patient-row-${p.patient_id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm flex-shrink-0">
                    {p.name[0]}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{p.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {p.cpf && <p className="text-xs text-muted-foreground">CPF: {p.cpf}</p>}
                      <span className="text-xs text-muted-foreground">
                        {p.active_wounds_count || 0} lesão(ões) ativa(s)
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {p.max_risk && p.max_risk !== "baixo" && (
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full risk-${p.max_risk}`}>
                      {RISK_LABELS[p.max_risk]}
                    </span>
                  )}
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > limit && (
          <div className="flex items-center justify-center gap-3 animate-fade-in">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-4 py-2 text-sm border border-border rounded-lg disabled:opacity-40 hover:bg-secondary transition-all"
            >
              Anterior
            </button>
            <span className="text-sm text-muted-foreground">
              {page + 1} / {Math.ceil(total / limit)}
            </span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={(page + 1) * limit >= total}
              className="px-4 py-2 text-sm border border-border rounded-lg disabled:opacity-40 hover:bg-secondary transition-all"
            >
              Próximo
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}
